import React, { useState } from 'react';
import { ChatArtifact } from '../../services/artifactDetector';
import { Card } from '../ui/Card';
import { IssueTableArtifact } from './IssueTableArtifact';
import { IssueDetailArtifact } from './IssueDetailArtifact';
import { SprintSummaryArtifact } from './SprintSummaryArtifact';
import { BoardOverviewArtifact } from './BoardOverviewArtifact';
import { ProjectCardArtifact } from './ProjectCardArtifact';
import { SpacesListArtifact } from './SpacesListArtifact';
import { PagesListArtifact } from './PagesListArtifact';
import { IssueDistributionArtifact } from './IssueDistributionArtifact';
import { GanttChartArtifact } from './GanttChartArtifact';
import { BurndownChartArtifact } from './BurndownChartArtifact';
import { ChevronDown, ChevronUp, Maximize2, Minimize2 } from 'lucide-react';
interface ArtifactRendererProps {
  artifact: ChatArtifact;
}
export function ArtifactRenderer({
  artifact
}: ArtifactRendererProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const renderContent = () => {
    try {
      // Ensure data is safe before passing to child components
      const safeData = artifact.data;
      if (safeData === null || safeData === undefined) {
        return <div className="p-4 text-sm text-slate-400 bg-slate-900 rounded-lg border border-slate-800" data-id="element-34">
            No hay datos para mostrar.
          </div>;
      }
      switch (artifact.type) {
        case 'issue_table':
          return <IssueTableArtifact data={Array.isArray(safeData) ? safeData : []} data-id="element-35" />;
        case 'issue_detail':
          return <IssueDetailArtifact data={safeData} data-id="element-36" />;
        case 'sprint_summary':
          return <SprintSummaryArtifact data={safeData} data-id="element-37" />;
        case 'board_overview':
          return <BoardOverviewArtifact data={Array.isArray(safeData) ? safeData : []} data-id="element-38" />;
        case 'project_card':
          return <ProjectCardArtifact data={safeData} data-id="element-39" />;
        case 'spaces_list':
          return <SpacesListArtifact data={Array.isArray(safeData) ? safeData : []} data-id="element-40" />;
        case 'pages_list':
          return <PagesListArtifact data={Array.isArray(safeData) ? safeData : []} data-id="element-41" />;
        case 'issue_distribution':
          return <IssueDistributionArtifact data={Array.isArray(safeData) ? safeData : []} data-id="element-42" />;
        case 'gantt_chart':
          return <GanttChartArtifact data={Array.isArray(safeData) ? safeData : []} data-id="element-43" />;
        case 'burndown_chart':
          return <BurndownChartArtifact data={safeData} data-id="element-44" />;
        default:
          return <div className="p-4 text-sm text-slate-400 bg-slate-900 rounded-lg border border-slate-800" data-id="element-45">
              Artifact type "{artifact.type}" not supported yet.
            </div>;
      }
    } catch (e) {
      console.error(`[ArtifactRenderer] Error rendering ${artifact.type}:`, e);
      return <div className="p-4 text-sm text-red-400 bg-red-950/30 rounded-lg border border-red-900/50" data-id="element-46">
          Error rendering artifact: {e.message}
        </div>;
    }
  };
  const header = <div className="flex items-center justify-between p-3 bg-slate-800/80 border-b border-slate-700/80 cursor-pointer hover:bg-slate-800 transition-colors" onClick={() => setIsExpanded(!isExpanded)} data-id="element-47">
      <div className="flex items-center gap-2" data-id="element-48">
        <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider" data-id="element-49">
          {artifact.title}
        </span>
        <span className="text-[10px] text-slate-500 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800" data-id="element-50">
          {artifact.type}
        </span>
      </div>
      <div className="flex items-center gap-1" data-id="element-51">
        <button onClick={e => {
        e.stopPropagation();
        setIsFullscreen(!isFullscreen);
      }} className="p-1 text-slate-500 hover:text-slate-300 hover:bg-slate-700 rounded transition-colors" title={isFullscreen ? 'Minimizar' : 'Pantalla completa'} data-id="element-52">
          {isFullscreen ? <Minimize2 size={14} data-id="element-53" /> : <Maximize2 size={14} data-id="element-54" />}
        </button>
        <button className="p-1 text-slate-500 hover:text-slate-300 hover:bg-slate-700 rounded transition-colors" data-id="element-55">
          {isExpanded ? <ChevronUp size={14} data-id="element-56" /> : <ChevronDown size={14} data-id="element-57" />}
        </button>
      </div>
    </div>;
  if (isFullscreen) {
    return <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-8 animate-in fade-in duration-200" data-id="element-58">
        <Card className="w-full max-w-6xl max-h-[90vh] flex flex-col bg-slate-900 border-slate-700 shadow-2xl shadow-black/50 overflow-hidden" data-id="element-59">
          {header}
          <div className="flex-1 overflow-auto p-6" data-id="element-60">{renderContent()}</div>
        </Card>
      </div>;
  }
  return <Card className="my-4 overflow-hidden bg-slate-900/50 border-slate-700/80 shadow-md" data-id="element-61">
      {header}
      {isExpanded && <div className="p-0 animate-in slide-in-from-top-2 duration-200" data-id="element-62">
          {renderContent()}
        </div>}
    </Card>;
}